import { CheckCircle2, ForwardChevrons, Keyboard, X } from '@griply/assets';
import {
  Habit,
  ScheduleEntryState,
  Task,
  TaskType,
  Timestamp,
} from '@griply/types';
import {
  getActiveSchedule,
  getOverdueDate,
  getScheduleByDate,
  getScheduleDayTarget,
} from '@griply/utils';
import { isSameDay } from 'date-fns';
import {
  useTaskTodayControls,
  useUpdateHabitScheduleEntry,
} from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PopupMenuList } from 'shared/components/ui/popup-menu';
import { QuickActionRow } from 'shared/components/ui/quick-action-row';
import { EntryTypes } from 'shared/components/ui/task-detail-checkbox/overdue-context-menu';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useOpenManualEntryLogging } from 'shared/contexts/habit-manual-entry-logging';
import { useToday } from 'shared/contexts/today';

import { OverdueEntryPrompt } from './types';

export type CompleteListProps = {
  task: Task;
  onClose: () => void;
  date?: Timestamp;
  onOverdue: (prompt: OverdueEntryPrompt) => void;
};

export const CompleteList: React.FC<CompleteListProps> = ({
  task,
  onClose,
  date,
  onOverdue,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { onComplete } = useTaskTodayControls();
  const updateHabitEntry = useUpdateHabitScheduleEntry();
  const openManualEntryLogging = useOpenManualEntryLogging();

  const completed = useMemo(() => {
    if (task.completedAt) {
      return true;
    }

    if (task.schedules) {
      const dateSchedule = getScheduleByDate(task.schedules, {
        date: date ?? today,
        today,
      });
      const entry = dateSchedule?.entries.find(
        (entry) => entry.date.getTime() === date?.getTime(),
      );
      const dayTarget = dateSchedule ? getScheduleDayTarget(dateSchedule) : 1;
      return (
        entry?.state === ScheduleEntryState.Complete &&
        (entry.value ?? 0) >= dayTarget
      );
    }
  }, [date, task.completedAt, task.schedules, today]);

  const overdueDate = useMemo(
    () => getOverdueDate({ task, dueDate: date, today }),
    [date, task, today],
  );

  const completeOnDate = (entryDate: Timestamp) => {
    const dateSchedule = getScheduleByDate(task.schedules, {
      date: entryDate,
      today,
    });
    const dayTarget = dateSchedule ? getScheduleDayTarget(dateSchedule) : 1;
    onComplete(task, ScheduleEntryState.Complete, {
      referenceDate: entryDate,
      value: completed ? 0 : dayTarget,
    });
  };

  const skipOnDate = (entryDate: Timestamp) =>
    updateHabitEntry(task as Habit, entryDate, ScheduleEntryState.Skip);

  const failOnDate = (entryDate: Timestamp) =>
    updateHabitEntry(task as Habit, entryDate, ScheduleEntryState.Failure);

  const logOnDate = (entryDate: Timestamp) =>
    openManualEntryLogging(task.id, entryDate);

  const runEntryAction = (
    type: EntryTypes,
    onDate: (entryDate: Timestamp) => void,
    canPrompt = true,
  ) => {
    if (overdueDate && canPrompt) {
      onOverdue({ type, overdueDate, onDate });
      return;
    }

    onDate(date ?? today);
    onClose();
  };

  const _onComplete = () =>
    runEntryAction(EntryTypes.Complete, completeOnDate, !completed);

  const onSkip = () => runEntryAction(EntryTypes.Skip, skipOnDate);

  const onFail = () => runEntryAction(EntryTypes.Failure, failOnDate);

  const onLog = () => runEntryAction(EntryTypes.Complete, logOnDate);

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);

  const completeRef = useRef<HTMLButtonElement>(null);
  const skipRef = useRef<HTMLButtonElement>(null);
  const failRef = useRef<HTMLButtonElement>(null);
  const logRef = useRef<HTMLButtonElement>(null);

  const completeLabel = t(
    task.completedAt
      ? 'context-menus.task.labels.un-complete'
      : 'context-menus.task.labels.complete',
  );

  const skippedTodayOrAfter = useMemo(
    () =>
      getActiveSchedule(task.schedules, today)?.entries.find(
        (entry) =>
          entry.state === ScheduleEntryState.Skip &&
          isSameDay(entry.date, today),
      ),
    [task.schedules, today],
  );
  const failedToday = useMemo(
    () =>
      getActiveSchedule(task.schedules, today)?.entries.find(
        (entry) =>
          entry.state === ScheduleEntryState.Failure &&
          isSameDay(entry.date, today),
      ),
    [task.schedules, today],
  );

  return (
    <PopupMenuList hasBottomBorder>
      <QuickActionRow label={t('context-menus.task.labels.complete')}>
        <IconButton
          ref={completeRef}
          icon={CheckCircle2}
          onClick={_onComplete}
          color={Colors.Success}
          highlighted={!!completed}
          aria-label={completeLabel}
        />
        <Tooltip referenceElement={completeRef} label={completeLabel} />
        {isHabit && !skippedTodayOrAfter && (
          <>
            <IconButton
              ref={skipRef}
              icon={ForwardChevrons}
              onClick={onSkip}
              color={Colors.Warning}
              aria-label={t('context-menus.task.labels.skip')}
            />
            <Tooltip
              referenceElement={skipRef}
              label={t('context-menus.task.labels.skip')}
            />
          </>
        )}
        {isHabit && !failedToday && (
          <>
            <IconButton
              ref={failRef}
              icon={X}
              onClick={onFail}
              color={Colors.Error}
              aria-label={t('context-menus.task.labels.fail')}
            />
            <Tooltip
              referenceElement={failRef}
              label={t('context-menus.task.labels.fail')}
            />
          </>
        )}
        {isHabit && (
          <>
            <IconButton
              ref={logRef}
              icon={Keyboard}
              onClick={onLog}
              color={Colors.Default}
              aria-label={t('context-menus.task.labels.log-progress')}
            />
            <Tooltip
              referenceElement={logRef}
              label={t('context-menus.task.labels.log-progress')}
            />
          </>
        )}
      </QuickActionRow>
    </PopupMenuList>
  );
};
